import { useState } from 'react';
import { useNavigate } from 'react-router';
import { TooltipProvider } from '@monksflow/monks-ui';
import { motion } from 'motion/react';
import { FolderTreeIcon, RouteIcon, UsersIcon } from 'lucide-react';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { QuickEditProjectModal, useDeletePublishedProject } from '@/features/edit-project';
import { StaggerItem } from '@/shared/ui/animations';
import { AboutPageDialog } from '@/shared/ui';
import {
  ListPageBody,
  ListPageLayout,
  PageHeader,
  PageHeaderIdentity,
  PageHeaderTitle,
} from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import {
  ProjectsTable,
  ProjectsToolbar,
  ProjectsViewState,
  resolveProjectsViewState,
  useProjectsList,
  type ProjectsTableEditDeps,
} from '@/widgets/projects-list';
import { useTranslation } from 'react-i18next';

/**
 * Projects page — fully v4/monks-ui, zero MUI.
 *
 * Matches the Beat UI "Project overview" design.
 *
 * The page is rendered inside the unified `DashboardLayout` shell (mounted
 * ONCE at the dashboard route group in `AppRoutes.tsx`). Since the shell
 * is full-bleed by design, this page owns its own padded wrapper at the
 * root (`page-layout`). The
 * `dashboard-layout-padded` testid is preserved on the wrapper for
 * backward compat with tests that target the padded contract.
 */
/**
 * Page-local hook factory: closes over the current workspace id so the
 * widget receives a 0-arg hook reference. The widget calls this hook
 * unconditionally at the top of `<ProjectsTable/>`, satisfying React's
 * rules-of-hooks contract.
 */
const useDeleteProjectForCurrentWorkspace = (): ReturnType<typeof useDeletePublishedProject> => {
  const workspaceId = useCurrentWorkspaceId();
  return useDeletePublishedProject({ workspaceId });
};

/**
 * Phase D wiring: project-edit feature deps (modal + delete mutation)
 * lifted here at the page boundary so the `widgets/projects-list` slice
 * stays decoupled from `features/edit-project`. The widget only sees
 * opaque slots (component + bound hook). Frozen at module scope —
 * neither dep depends on per-render state, so a stable reference is
 * safe and avoids needless re-renders.
 */
const PROJECTS_TABLE_EDIT_DEPS: ProjectsTableEditDeps = {
  useDeletePublishedProject: useDeleteProjectForCurrentWorkspace,
  QuickEditProjectModal,
};

export function Projects() {
  const { t } = useTranslation('projects');
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const navigate = useNavigate();
  const projects = useProjectsList();

  const navigateToNewProject = () => void navigate(ROUTES.PROJECT_NEW);
  const navigateToProject = (platformProjectId: string) =>
    void navigate(ROUTES.PROJECT_DETAIL(platformProjectId));

  const handlePageChange = (newPage: number) => {
    projects.handleChangePage(null, newPage);
  };

  const viewState = resolveProjectsViewState({
    isLoading: projects.isLoading,
    isError: projects.isError,
    rows: projects.rows,
    hasSearch: projects.hasSearch,
    hasActiveFilters: projects.hasActiveFilters,
    onNewProject: navigateToNewProject,
    onClearFilters: projects.handleClearFilters,
  });

  const showToolbar = viewState.type === 'data' || viewState.type === 'no-results';

  return (
    <TooltipProvider>
      <motion.div
        data-testid="dashboard-layout-padded"
        className="page-layout"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ListPageLayout>
          {/* Staggered entrance (MFINS-3093): header → description → filters →
              content cascade on the shared 50ms rhythm via `StaggerItem`. */}
          <StaggerItem index={0}>
            <PageHeader>
              <PageHeaderIdentity>
                <PageHeaderTitle info={{ onClick: () => setIsAboutOpen(true) }}>
                  {t('projects.projects')}
                </PageHeaderTitle>
              </PageHeaderIdentity>
            </PageHeader>
          </StaggerItem>

          <ListPageBody>
            {showToolbar && (
              <StaggerItem index={2}>
                <ProjectsToolbar
                  searchValue={projects.searchInput}
                  onSearchChange={projects.setSearchInput}
                  selectedType={projects.selectedType}
                  onTypeChange={projects.handleTypeChange}
                  sortId={projects.sortId}
                  onSortChange={projects.handleSortChange}
                  onNewProject={navigateToNewProject}
                />
              </StaggerItem>
            )}

            <StaggerItem index={3} className="flex min-h-0 flex-1 flex-col">
              {viewState.type === 'data' ? (
                <ProjectsTable
                  rows={projects.rows}
                  isLoading={projects.isLoading}
                  totalCount={projects.totalCount}
                  page={projects.page}
                  rowsPerPage={projects.rowsPerPage}
                  onRowClick={navigateToProject}
                  onPageChange={handlePageChange}
                  editProject={PROJECTS_TABLE_EDIT_DEPS}
                />
              ) : (
                <ProjectsViewState state={viewState} />
              )}
            </StaggerItem>
          </ListPageBody>
        </ListPageLayout>

        <AboutPageDialog
          open={isAboutOpen}
          onOpenChange={setIsAboutOpen}
          title={t('projects.about.title')}
          description={t('projects.about.description')}
          rows={[
            { icon: FolderTreeIcon, text: t('projects.about.tiedToTheWork') },
            { icon: UsersIcon, text: t('projects.about.sharedStartingPoint') },
            { icon: RouteIcon, text: t('projects.about.kickoffToDelivery') },
          ]}
        />
      </motion.div>
    </TooltipProvider>
  );
}
